import { ActionEngine } from "./actions.js";
import { RefRegistry } from "./refs.js";
import { SafetyLayer } from "./safety.js";
import { SnapshotEngine } from "./snapshot.js";
import { summarizeResult, ToolExecutor, } from "./tools.js";
import { defaultPolicy, } from "./types.js";
/**
 * The observe → decide → act loop. Given a Planner (mock/heuristic or a remote
 * model-backed one), it refreshes an observation, asks the planner for the next
 * tool calls, executes them through the safety-gated tool layer, and repeats.
 */
export class QuillAgent {
    opts;
    registry = new RefRegistry();
    policy;
    snapshot;
    actions;
    safety;
    executor;
    sink;
    root;
    stopped = false;
    constructor(opts) {
        this.opts = opts;
        this.policy = { ...defaultPolicy(), ...opts.policy };
        this.root = opts.root ?? (() => document.body);
        this.sink = opts.onActivity ?? (() => { });
        this.snapshot = new SnapshotEngine(this.root, this.registry, this.policy);
        this.safety = new SafetyLayer(this.policy);
        this.actions = new ActionEngine(this.registry, this.safety, opts.confirm);
        const ctx = {
            snapshot: this.snapshot,
            actions: this.actions,
            registry: this.registry,
            root: this.root,
            confirm: opts.confirm,
            now: () => Date.now(),
        };
        this.executor = new ToolExecutor(ctx);
        opts.signal?.addEventListener("abort", () => (this.stopped = true));
    }
    stop() {
        this.stopped = true;
    }
    async run(task) {
        const history = [{ role: "user", content: task }];
        const deadline = Date.now() + this.policy.maxTaskSeconds * 1000;
        this.sink({ type: "status", status: "working" });
        for (let step = 0; step < this.policy.maxSteps; step++) {
            if (this.stopped)
                return this.finish("stopped", step, history);
            if (Date.now() > deadline)
                return this.finish("timeout", step, history);
            const observation = this.snapshot.snapshot({ mode: "interactive" }).text;
            let plan;
            try {
                plan = await this.opts.planner.next({ task, history, observation, step });
            }
            catch (err) {
                this.sink({ type: "status", status: "error", detail: String(err) });
                return this.finish("error", step, history);
            }
            if (plan.text) {
                history.push({ role: "assistant", content: plan.text });
                this.sink({ type: "assistant_text", text: plan.text });
            }
            for (const call of plan.toolCalls) {
                if (this.stopped)
                    return this.finish("stopped", step, history);
                this.sink({ type: "tool_call", id: call.id, name: call.name, input: call.input });
                if (typeof call.input?.ref === "string") {
                    const ref = call.input.ref;
                    this.sink({ type: "highlight", ref, kind: call.name === "read_element" ? "read" : "act" });
                }
                const result = await this.executor.execute(call.name, call.input);
                const { ok, summary } = summarizeResult(call.name, result);
                this.sink({ type: "tool_result", id: call.id, name: call.name, ok, summary });
                history.push({
                    role: "tool",
                    toolName: call.name,
                    toolCallId: call.id,
                    content: JSON.stringify(result),
                });
            }
            if (plan.done)
                return this.finish("done", step + 1, history);
        }
        return this.finish("max_steps", this.policy.maxSteps, history);
    }
    finish(status, steps, history) {
        this.sink({
            type: "status",
            status: status === "done" ? "idle" : status === "stopped" ? "stopped" : "error",
            detail: status,
        });
        return { status, steps, history };
    }
}
export { defaultPolicy };
//# sourceMappingURL=agent.js.map